import { FunctionComponent } from 'react';
import { Typography } from '@material-ui/core';
import Box from '@material-ui/core/Box';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { MarketplaceTax } from '@spike/marketplace-model';
import { Product } from '@spike/product-model';
import { PriceField2 } from 'components/UI';
import Switch from 'components/UI/V2/Switch';
import { TaxesSelector } from 'components/UI/TaxesSelector';
import ButtonV3 from 'components/UI/V3/Button/Button';
import clone from 'lodash/clone';
import clsx from 'clsx';

interface Props {
    product: Product;
    onChange?: (product: Product) => void;
    onNext: () => void;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        container: {
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            maxWidth: '100%',
            minWidth: 0
        },
        titleSection: {
            display: 'flex',
            justifyContent: 'flex-end',
            width: '100%',
            marginBottom: '30px',
            [theme.breakpoints.down(768)]: {
                justifyContent: 'flex-start',
                marginBottom: '16px'
            }
        },
        horizontalContainer: {
            display: 'flex',
            alignItems: 'center',
            [theme.breakpoints.down(768)]: {
                marginBottom: '16px',
                flexWrap: 'wrap'
            },
            [theme.breakpoints.between(768, 'md')]: {
                marginBottom: '39px'
            },
            [theme.breakpoints.up('lg')]: {
                marginBottom: '48px'
            }
        },
        marginNone: {
            marginBottom: '0px !important'
        },
        barcodeCategoryContainer: {
            display: 'flex',
            flexDirection: 'column',
            width: '220px',
            maxWidth: '100%',
            minWidth: 0,
            [theme.breakpoints.down(768)]: {
                width: '100%'
            }
        },
        paddingRight: {
            [theme.breakpoints.down(768)]: {
                paddingRight: 0
            },
            [theme.breakpoints.between(768, 'md')]: {
                paddingRight: '16px'
            },
            [theme.breakpoints.up('lg')]: {
                paddingRight: '20px'
            }
        },
        checkout: {
            [theme.breakpoints.down('md')]: {
                fontSize: '13px'
            },
            [theme.breakpoints.up('lg')]: {
                fontSize: '16px'
            }
        },
        checkoutPadding: {
            [theme.breakpoints.down('md')]: {
                paddingRight: '5px',
                paddingLeft: '11px'
            },
            [theme.breakpoints.up('lg')]: {
                paddingRight: '7px',
                paddingLeft: '14px'
            }
        },
        actions: {
            display: 'flex',
            justifyContent: 'flex-start',
            width: '100%',
            [theme.breakpoints.down(768)]: {
                'marginTop': '16px',
                'marginBottom': '8px',
                '& > button': {
                    width: '100%'
                }
            },
            [theme.breakpoints.between(768, 'md')]: {
                marginTop: '24px',
                marginBottom: '39px'
            },
            [theme.breakpoints.up('lg')]: {
                marginTop: '32px',
                marginBottom: '48px'
            }
        }
    })
);

export const Pricing: FunctionComponent<Props> = props => {
    const classes = useStyles();

    const availableForCheckoutChangeHandler = (_name: string, checked: boolean) => {
        const updatedProduct = clone(props.product);
        updatedProduct.businessProduct!.availableForCheckout = checked;
        props.onChange && props.onChange(updatedProduct);
    };

    const priceChangeHandler = (price: number | undefined) => {
        const updatedProduct = clone(props.product);
        updatedProduct.businessProduct!.pricing.price = price;

        if (updatedProduct.createdFromScratch) {
            updatedProduct.price = price || 0;
        }

        props.onChange && props.onChange(updatedProduct);
    };

    const taxesChangeHandler = (taxes: Array<MarketplaceTax>): void => {
        const updatedProduct = clone(props.product);
        updatedProduct.businessProduct!.pricing.taxes = [...taxes];
        props.onChange && props.onChange(updatedProduct);
    };

    return (
        <Box className={classes.container}>
            <Box className={classes.titleSection}>
                <Box className={clsx(classes.horizontalContainer, classes.marginNone)}>
                    <Typography className={clsx(classes.checkout, classes.checkoutPadding)}>
                        Available for checkout?
                    </Typography>
                    <Box className={clsx(classes.checkout, classes.checkoutPadding)}>
                        <Switch
                            name="availableForCheckout"
                            checked={props.product.businessProduct?.availableForCheckout || false}
                            onCheck={availableForCheckoutChangeHandler}
                        />
                    </Box>
                </Box>
            </Box>
            <Box className={classes.horizontalContainer}>
                <Box className={clsx(classes.paddingRight, classes.barcodeCategoryContainer)}>
                    <PriceField2
                        label="Retail Price"
                        name="price"
                        value={
                            props.product.businessProduct?.pricing?.price !== undefined &&
                            props.product.businessProduct?.pricing?.price !== null
                                ? Number(props.product.businessProduct.pricing.price)
                                : undefined
                        }
                        placeholder="0.00"
                        onChange={priceChangeHandler}
                        scale={2}
                        precision={6}
                    />
                </Box>
            </Box>
            <TaxesSelector
                checked={(props.product.businessProduct?.pricing?.taxes?.length || 0) > 0}
                selectedTaxes={props.product.businessProduct?.pricing?.taxes ?? []}
                onChange={taxesChangeHandler}
                title="Taxable Product"
            />
            <Box className={classes.actions}>
                <ButtonV3
                    label="Next"
                    onClick={props.onNext}
                    size="large"
                    variant="primary"
                />
            </Box>
        </Box>
    );
};

export default Pricing;
